import i18next from "i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FIELD_REQUIRED,
  LOADING,
  PRODUCTS,
} from "../../../../i18n/keysTranslations";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetProductSetsCatalogAdsCampaigns } from "../../../../actions/adsCampaigns";
import { TIKTOK } from "../../../../utils/constants";
import _ from "lodash";
import { textFieldInputSx } from "../../../../styles";

const PRODUCT_SETS_PAGE_SIZE = 20;

const getProductSetOptionLabel = (option, t) => {
  if (option?.count != null) {
    return `${option?.name} - ${option?.id} - (${option?.count} ${t(PRODUCTS)})`;
  }
  return `${option?.name} - ${option?.id}`;
};

const SelectProductsSetTikTok = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  catalogID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  disableClearable = false,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadingMoreRef = useRef(false);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const extractProductSets = useCallback((data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
      const first = data[0];
      if (first?.productSets) {
        return first.productSets;
      }
      return data;
    }
    return data?.productSets ?? data?.data ?? [];
  }, []);

  const onGetProductSets = useCallback(
    async (pageNum = 1, append = false) => {
      if (!integrationID || !adAccountID || !catalogID) {
        setOptions([]);
        return;
      }
      if (append) {
        loadingMoreRef.current = true;
      } else {
        setLoading(true);
      }
      const response = await dispatch(
        startGetProductSetsCatalogAdsCampaigns({
          integrationID,
          adAccountID,
          catalogID,
          lastKey: pageNum,
          platform: TIKTOK,
        })
      );
      if (response.ok) {
        const data = response.data;
        const payload = Array.isArray(data) ? data : (data?.data ?? data);
        const items = extractProductSets(payload);
        const itemsArray = Array.isArray(items) ? items : [];
        const hasMoreData =
          data?.hasMore ?? itemsArray.length >= PRODUCT_SETS_PAGE_SIZE;
        setHasMore(hasMoreData);
        if (append) {
          setOptions((prev) => _.uniqBy([...prev, ...itemsArray], "id"));
        } else {
          setOptions(itemsArray?.length > 0 ? itemsArray : []);
        }
      }
      if (append) {
        loadingMoreRef.current = false;
      } else {
        setLoading(false);
      }
    },
    [integrationID, adAccountID, catalogID, dispatch, extractProductSets]
  );

  const onLoadMore = useCallback(() => {
    if (!loadingMoreRef.current && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      onGetProductSets(nextPage, true);
    }
  }, [hasMore, loading, page, onGetProductSets]);

  const onScrollListbox = useCallback(
    (event) => {
      const listboxNode = event.currentTarget;
      const scrollBottom =
        listboxNode.scrollHeight -
        listboxNode.scrollTop -
        listboxNode.clientHeight;
      if (scrollBottom < 50 && hasMore && !loading && !loadingMoreRef.current) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  useEffect(() => {
    if (integrationID && adAccountID && catalogID) {
      setPage(1);
      setHasMore(true);
      onGetProductSets(1, false);
    } else {
      setOptions([]);
    }
    // eslint-disable-next-line
  }, [integrationID, adAccountID, catalogID]);

  return (
    <Controller
      defaultValue={""}
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <Autocomplete
          {...field}
          fullWidth={fullWidth}
          disabled={disabled}
          disableClearable={disableClearable}
          filterOptions={(x) => x}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={20} />
              {t(LOADING)}
            </Box>
          }
          loading={loading}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(_, value) => {
            if (value?.id) {
              field.onChange(value.id);
              onChangeAction(value);
            } else {
              field.onChange(null);
              onChangeAction(null);
            }
          }}
          isOptionEqualToValue={(option, value) => option?.id === value}
          getOptionLabel={(option) => {
            const optionValue = options.find((opt) => opt.id === option);
            if (optionValue) {
              return getProductSetOptionLabel(optionValue, t);
            }
            if (typeof option === "string") {
              return option;
            }
            return getProductSetOptionLabel(option, t);
          }}
          options={options}
          ListboxProps={{
            onScroll: onScrollListbox,
          }}
          renderOption={(props, option) => (
            <Box
              {...props}
              component="li"
              key={option.id}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <Typography variant="body2">
                {getProductSetOptionLabel(option, t)}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              id={name}
              {...params}
              label={label}
              hiddenLabel={!label}
              variant={variant}
              placeholder={field?.value?.length > 0 ? "" : placeholder}
              error={!!error}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{!!error ? error?.message : helperText}</span>
                </Box>
              }
              sx={{
                ...textFieldInputSx,
                ...sxTextField,
                "& .MuiInputBase-root": {
                  ...(textFieldInputSx["& .MuiInputBase-root"] || {}),
                  height: 70,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default SelectProductsSetTikTok;
